import React, { useMemo } from 'react';
import styled from 'styled-components';
import { SearchInput } from '../../../../../generic/ui/SearchInput';
import { DateRangePicker } from '../../../../../generic/ui/DatePicker';
import { FilterPopover } from '../../../../../generic/ui/FilterPopover';
import { FormField } from '../../../../../generic/ui/FormLayout';
import WarehouseSelector from '../../../../../generic/ui/Selectors/WarehouseSelector';
import OrderStatusSelector, { OrderStatusValue } from '../../../../../generic/ui/Selectors/OrderStatusSelector';
import OrderGroupSelector from '../../../../../generic/ui/Selectors/OrderGroupSelector';
import OrderAkpacaTypeSelector, {
    OrderAkpacaTypeValue,
} from '../../../../../generic/ui/Selectors/OrderAkpacaTypeSelector';
import { IWarhouseListItem } from '../../../../../modules/API/Defaults/models/warhouseList';
import { ProductGroup } from '../types';
import { countActiveOrderFilters } from '../helpers/orderFilter';

interface Props {
    input: string;
    setInput: (value: string) => void;
    setSelectedDateStart: (value: string | null) => void;
    setSelectedDateEnd: (value: string | null) => void;
    statusId: OrderStatusValue;
    onStatusChange: (value: OrderStatusValue) => void;
    warehouseID: number;
    onWarehouseChange: (id: number) => void;
    warehouseList: IWarhouseListItem[] | null;
    typeFilter: OrderAkpacaTypeValue;
    onTypeChange: (value: OrderAkpacaTypeValue) => void;
    groupID: number;
    onGroupChange: (groupID: number) => void;
    groups: ProductGroup[];
}

/** Client orders toolbar: search, delivery date range and the status / warehouse / הקפצה filter popover. */
const OrdersSelectBar: React.FC<Props> = ({
    input,
    setInput,
    setSelectedDateStart,
    setSelectedDateEnd,
    statusId,
    onStatusChange,
    warehouseID,
    onWarehouseChange,
    warehouseList,
    typeFilter,
    onTypeChange,
    groupID,
    onGroupChange,
    groups,
}) => {
    // Default delivery range: today … +4 days (as before).
    const defaultRange = useMemo(() => {
        const today = new Date();
        const end = new Date();
        end.setDate(end.getDate() + 4);
        return [today, end];
    }, []);

    const selectedWarehouse = useMemo(
        () => warehouseList?.find((w) => w.WarehouseID === warehouseID) ?? null,
        [warehouseList, warehouseID],
    );

    const activeFiltersCount = countActiveOrderFilters({
        isWarehouseFilterVisible: !!warehouseList,
        warehouseID,
        typeFilter,
        statusId,
        groupID,
    });

    return (
        <SelectBarShell>
            <SearchInput value={input} onChange={(e) => setInput(e.target.value)} placeholder='חיפוש' />
            <DateRangePicker
                selected={defaultRange}
                // DateRangePicker only ever passes a yyyyMMdd string.
                setDate={(value) => typeof value === 'string' && setSelectedDateStart(value)}
                setDateEnd={(value) => typeof value === 'string' && setSelectedDateEnd(value)}
            />
            <FilterPopover label='סינון' count={activeFiltersCount}>
                {warehouseList && (
                    <FormField label='מחסן' fullWidth>
                        <WarehouseSelector
                            warehouses={warehouseList}
                            value={selectedWarehouse}
                            onSelect={(w) => onWarehouseChange(w ? w.WarehouseID : 0)}
                            allowClear
                            clearLabel='כל המחסנים'
                        />
                    </FormField>
                )}
                <FormField label='סטטוס' fullWidth>
                    <OrderStatusSelector value={statusId} onSelect={onStatusChange} />
                </FormField>
                <FormField label='קבוצה' fullWidth>
                    <OrderGroupSelector groups={groups} value={groupID} onSelect={onGroupChange} />
                </FormField>
                <FormField label='הקפצה' fullWidth>
                    <OrderAkpacaTypeSelector value={typeFilter} onSelect={onTypeChange} />
                </FormField>
            </FilterPopover>
        </SelectBarShell>
    );
};

export default OrdersSelectBar;

const SelectBarShell = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
`;
